/**
 * Form for collecting organization information during sign up
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { withStyles } from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';

import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import BusyButton from 'components/BusyButton/BusyButton';
import { email, minLength, maxLength, required } from 'services/validationMsgs';

const minLength10 = minLength(10);
const maxLength20 = maxLength(20);
const maxLength128 = maxLength(128);

/**
 * Determine if the form has any errors aside from an organization error regarding
 * an existing organization and a form-level error.
 *
 * @param {object} formErrors - Form errors to examine
 * @returns {boolean} - Whether the formErrors contain any errors other than
 *  an existing organization error and a form-level error
 */
const hasNonOrganizationError = (formErrors) => {
  const errProps = ['organization', '_error'];
  return (
    formErrors &&
    (!formErrors.organization ||
      Object.keys(formErrors).filter((key) => !errProps.includes(key)).length ||
      !formErrors.organization.match(/already assigned to an organization/g))
  );
};

const propTypes = {
  classes: PropTypes.shape({
    field: PropTypes.string.isRequired,
    next: PropTypes.string.isRequired,
    error: PropTypes.string.isRequired,
  }).isRequired,
  className: PropTypes.string.isRequired,
  error: PropTypes.string,
  handleOrgCreate: PropTypes.func.isRequired,
  handleOrgCreateSuccess: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitSucceeded: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
};

const defaultProps = {
  error: null,
};

export function UnstyledOrgForm({
  classes,
  className,
  error,
  handleOrgCreate,
  handleOrgCreateSuccess,
  handleSubmit,
  pristine,
  submitSucceeded,
  submitting,
  valid,
}) {
  const handleFormSubmissionReturn = (result) => {
    if (result.error) {
      // If user is already associated with an org, then don't trigger invalidation
      if (hasNonOrganizationError(result.formErrors)) {
        throw new SubmissionError(result.formErrors);
      }
    } else {
      handleOrgCreateSuccess();
    }
  };

  const submitHandler = (data) => handleOrgCreate(data).then(handleFormSubmissionReturn);

  return (
    <form onSubmit={handleSubmit(submitHandler)} className={className}>
      <Field
        name="primaryEmail"
        component={WrappedTextField}
        label="Contact Email"
        validate={[required, email, maxLength128]}
        className={classes.field}
        fullWidth
        helperText="We'll send important notifications for your organization to this address."
      />
      <Field
        name="companyName"
        component={WrappedTextField}
        label="Organization Name"
        className={classes.field}
        fullWidth
        validate={[required, maxLength128]}
      />
      <Field
        name="phoneNumber"
        component={WrappedTextField}
        label="Phone Number"
        fullWidth
        className={classes.field}
        validate={[minLength10, maxLength20]}
      />
      {error && !submitting ? (
        <Typography color="error" className={classes.error}>
          {error}
        </Typography>
      ) : null}
      <BusyButton
        size="large"
        type="submit"
        fullWidth
        className={classes.next}
        color="primary"
        variant="contained"
        loading={submitting}
        disabled={!valid || pristine || submitSucceeded}
      >
        Next
      </BusyButton>
    </form>
  );
}

UnstyledOrgForm.propTypes = propTypes;
UnstyledOrgForm.defaultProps = defaultProps;

const styles = (theme) => ({
  field: {
    marginTop: theme.spacing(2),
  },
  next: {
    marginTop: theme.spacing(4),
  },
  error: {
    marginTop: theme.spacing(2),
  },
});

export default withStyles(styles)(
  reduxForm({
    form: 'orgInfoSignUp',
    enableReinitialize: true,
  })(UnstyledOrgForm),
);
